import { Emitter } from "@core/events/emitter";
import type { Vec3 } from "@models/data/entityData";
import { DEFAULT_CUTTING_PLANE, type CuttingPlaneState } from "@models/data/sceneInfo";
import type { IProjectModel } from "@models/project/IProjectModel";
import {
  forwardOfEuler,
  GIZMO_ROTATION_Y180,
  lookRotationEuler,
  quatMultiply,
  unityEulerToQuat,
  unityQuatToEuler,
} from "./cuttingPlaneMath";
import type {
  CuttingPlaneAxisMode,
  CuttingPlanePose,
  ICommonSliceToolModel,
} from "./ICommonSliceToolModel";

/**
 * `CommonSliceToolModel` — port of `Models/Tools/CommonSliceToolModel.cs`.
 * Headless: persisted truth is `SceneInfo.cuttingPlane` on the active scene
 * (single writer: `IProjectModel.updateSceneCuttingPlaneAsync`); this model
 * adds the transient gizmo preview, axis-mode state, and the Unity-shaped
 * event surface. See `ICommonSliceToolModel` for the pose-space contract.
 */
export class CommonSliceToolModel implements ICommonSliceToolModel {
  readonly onCommonSliceChanged = new Emitter<boolean>();
  readonly onAxisChanged = new Emitter<CuttingPlaneAxisMode>();
  readonly onStateChanged = new Emitter<void>();

  isSliceGizmoEnabled = false;

  private m_previewPose: CuttingPlanePose | null = null;
  private m_axisMode: CuttingPlaneAxisMode = "free";
  private readonly m_offFns: Array<() => void> = [];

  constructor(private readonly m_project: IProjectModel) {
    this.m_offFns.push(
      this.m_project.onActiveSceneChanged.on(() => {
        // Unity destroys + recreates the plane GO across scene loads and the
        // inspector always reopens in Free mode.
        this.m_previewPose = null;
        this.m_axisMode = "free";
        this.onStateChanged.emit();
      }),
      this.m_project.onSceneChangesApplied.on(({ sceneId }) => {
        if (sceneId === this.m_project.activeSceneId) this.onStateChanged.emit();
      }),
      this.m_project.onActiveProjectChanged.on(() => {
        this.m_previewPose = null;
        this.m_axisMode = "free";
        this.isSliceGizmoEnabled = false;
        this.onStateChanged.emit();
      }),
    );
  }

  dispose(): void {
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
    this.onCommonSliceChanged.clear();
    this.onAxisChanged.clear();
    this.onStateChanged.clear();
  }

  get exists(): boolean {
    return this.activeState().exist;
  }

  get visible(): boolean {
    return this.activeState().visible;
  }

  get pose(): CuttingPlanePose {
    if (this.m_previewPose) return this.m_previewPose;
    const state = this.activeState();
    return { position: state.position, euler: state.euler };
  }

  get usePartColor(): boolean {
    return this.activeState().usePartColor;
  }

  get axisMode(): CuttingPlaneAxisMode {
    return this.m_axisMode;
  }

  async initialCommonSliceAsync(anchor?: Vec3): Promise<void> {
    const state = this.activeState();
    if (state.exist && state.visible) return;
    const created = !state.exist;
    // Unity: plane at `IPlacementModel.AnchorPoint`, visual forward = root
    // up ⇒ persisted rotation = LookRotation(up) ∘ Ry(180) — a horizontal
    // cut discarding everything above the anchor.
    const patch: Partial<CuttingPlaneState> = created
      ? {
          visible: true,
          exist: true,
          position: anchor ?? [0, 0, 0],
          euler: eulerForVisualForward([0, 1, 0]),
        }
      : { visible: true, exist: true };
    await this.persist(patch);
    this.onCommonSliceChanged.emit(true);
  }

  previewPose(pose: CuttingPlanePose): void {
    this.m_previewPose = pose;
    this.onStateChanged.emit();
  }

  async commitPoseAsync(): Promise<void> {
    const preview = this.m_previewPose;
    if (!preview) return;
    this.m_previewPose = null;
    await this.persist({ visible: true, position: preview.position, euler: preview.euler });
  }

  async setPoseAsync(pose: CuttingPlanePose): Promise<void> {
    this.m_previewPose = null;
    await this.persist({ visible: true, position: pose.position, euler: pose.euler });
  }

  async setVisibleAsync(visible: boolean): Promise<void> {
    if (visible) {
      // Unity `OnVisibilityChanged` (eye on): persist current pose + Visible.
      const pose = this.pose;
      await this.persist({ visible: true, position: pose.position, euler: pose.euler });
    } else {
      // Unity `DeleteCommonSlice`: Visible=false, Exist stays.
      this.m_previewPose = null;
      await this.persist({ visible: false });
    }
    this.onCommonSliceChanged.emit(true);
  }

  async removeCommonSliceAsync(): Promise<void> {
    this.m_previewPose = null;
    this.isSliceGizmoEnabled = false;
    await this.persist({ visible: false, exist: false });
    this.onCommonSliceChanged.emit(false);
  }

  async flipCommonSliceAsync(): Promise<void> {
    // Unity assigns `transform.forward = -transform.forward` on the visual
    // root; the root forward is the NEGATED persisted forward.
    const visualForward = negate(forwardOfEuler(this.pose.euler));
    await this.setVisualForwardAsync(negate(visualForward));
  }

  async flipToAxisAsync(axis: "x" | "y" | "z"): Promise<void> {
    const direction: Vec3 = axis === "x" ? [1, 0, 0] : axis === "y" ? [0, 1, 0] : [0, 0, 1];
    this.m_axisMode = axis;
    await this.setVisualForwardAsync(direction);
    this.onAxisChanged.emit(axis);
  }

  freeMode(): void {
    this.m_axisMode = "free";
    this.onAxisChanged.emit("free");
  }

  async setUsePartColorAsync(value: boolean): Promise<void> {
    await this.persist({ usePartColor: value });
  }

  /** Visual-root forward → persisted euler (`LookRotation(f) ∘ Ry(180)`). */
  private async setVisualForwardAsync(visualForward: Vec3): Promise<void> {
    const euler = eulerForVisualForward(visualForward);
    this.m_previewPose = null;
    await this.persist({ euler });
  }

  private async persist(patch: Partial<CuttingPlaneState>): Promise<void> {
    const sceneId = this.m_project.activeSceneId;
    if (!sceneId) return;
    await this.m_project.updateSceneCuttingPlaneAsync(sceneId, patch);
  }

  private activeState(): CuttingPlaneState {
    const sceneId = this.m_project.activeSceneId;
    const scene = sceneId
      ? this.m_project.scenes.find((s) => s.id === sceneId) ?? null
      : null;
    return scene?.cuttingPlane ?? DEFAULT_CUTTING_PLANE;
  }
}

/**
 * Persisted euler whose live visual root faces `visualForward` — the web
 * equivalent of Unity assigning `transform.forward` and letting
 * `UpdatePose(..., includesGizmoRotation: true)` strip the 180°-Y offset.
 */
function eulerForVisualForward(visualForward: Vec3): Vec3 {
  const rootRot = unityEulerToQuat(lookRotationEuler(visualForward));
  return unityQuatToEuler(quatMultiply(rootRot, GIZMO_ROTATION_Y180));
}

function negate(v: Vec3): Vec3 {
  return [-v[0], -v[1], -v[2]];
}
